/**
 * @fileoverview Modal de sincronização de histórico WAHA por ticket.
 *
 * Fluxo: FORM (escolher dias + mídia) → SYNCING (progress + stats live via
 * socket) → COMPLETE (stats finais). Fechar durante SYNCING não cancela —
 * o sync continua no backend e termina mesmo sem modal aberto.
 */

import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Dialog,
  IconButton,
  Typography,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Switch,
  Collapse,
  makeStyles,
} from "@material-ui/core";
import {
  X,
  DownloadCloud,
  CheckCircle,
  AlertTriangle,
  Calendar,
} from "lucide-react";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { SocketContext } from "../../context/Socket/SocketContext";

const MODAL_FONT_FAMILY = '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif';

const STATES = { FORM: "FORM", SYNCING: "SYNCING", COMPLETE: "COMPLETE" };
const DAYS_PRESETS = [7, 30, 90];
const MAX_DAYS = 180;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      "& .MuiDialog-paper": {
        fontFamily: MODAL_FONT_FAMILY,
        width: "min(520px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 24,
        background: dark
          ? "linear-gradient(135deg, rgba(18, 18, 19, 0.98) 0%, rgba(22, 22, 24, 0.98) 100%)"
          : "#ffffff",
        boxShadow: dark
          ? "0 24px 80px rgba(0, 0, 0, 0.6)"
          : "0 24px 80px rgba(0, 0, 0, 0.12)",
        overflow: "hidden",
        position: "relative",
      },
      "& .MuiDialog-paper::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: "linear-gradient(90deg, #4096dc 0%, #1565c0 100%)",
        borderRadius: "24px 24px 0 0",
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "20px 24px 12px",
    },
    titleBlock: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    titleIconBox: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: dark ? "rgba(64,150,220,0.16)" : "#E6F2FB",
      color: "#4096dc",
    },
    title: {
      fontSize: "1.15rem",
      fontWeight: 700,
      color: dark ? "#f5f5f5" : "#1e293b",
    },
    closeBtn: {
      padding: 6,
      color: dark ? "#cbd5e1" : "#475569",
      "&:hover": {
        backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      },
    },
    body: {
      padding: "0 24px 20px",
    },
    description: {
      fontSize: 13,
      color: dark ? "#9199a3" : "#64748B",
      marginBottom: 16,
      lineHeight: 1.5,
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.14,
      textTransform: "uppercase",
      color: dark ? "#93a6ff" : "#3D5CFD",
      marginBottom: 8,
    },
    radioGroup: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
      margin: 0,
    },
    radioItem: {
      margin: 0,
      padding: "8px 10px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
      backgroundColor: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
      transition: "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
      "& .MuiFormControlLabel-label": {
        fontSize: 13,
        color: dark ? "#e6e8eb" : "#1e293b",
        transition: "color 160ms ease",
      },
    },
    radioItemActive: {
      borderColor: "#4096dc",
      backgroundColor: dark ? "rgba(64,150,220,0.08)" : "#E6F2FB",
      boxShadow: dark
        ? "0 0 0 3px rgba(64,150,220,0.12)"
        : "0 0 0 3px rgba(64,150,220,0.10)",
    },
    customField: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginTop: 10,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
      backgroundColor: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
      transition: "border-color 120ms ease, background-color 120ms ease",
      "&:focus-within": {
        borderColor: "#4096dc",
        backgroundColor: dark ? "rgba(64,150,220,0.08)" : "#E6F2FB",
      },
    },
    customFieldIconBox: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: dark ? "rgba(64,150,220,0.16)" : "#E6F2FB",
      color: "#4096dc",
      flexShrink: 0,
    },
    customFieldLabelBlock: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
    },
    customFieldLabel: {
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#e6e8eb" : "#1e293b",
    },
    customFieldHint: {
      fontSize: 11,
      color: dark ? "#8b94a3" : "#64748B",
    },
    customFieldInputWrap: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 8,
      backgroundColor: dark ? "rgba(255,255,255,0.04)" : "#ffffff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      flexShrink: 0,
    },
    customFieldInput: {
      width: 56,
      border: "none",
      outline: "none",
      background: "transparent",
      fontFamily: "inherit",
      fontSize: 15,
      fontWeight: 700,
      color: dark ? "#f5f5f5" : "#1e293b",
      textAlign: "right",
      padding: 0,
      MozAppearance: "textfield",
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
      },
    },
    customFieldSuffix: {
      fontSize: 12,
      fontWeight: 600,
      color: dark ? "#8b94a3" : "#64748B",
      letterSpacing: 0.1,
    },
    switchRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 16,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
      backgroundColor: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
    },
    switchLabel: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    switchLabelTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#e6e8eb" : "#1e293b",
    },
    switchLabelHint: {
      fontSize: 11,
      color: dark ? "#8b94a3" : "#64748B",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: "12px 24px 20px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    btnGhost: {
      padding: "8px 18px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"}`,
      background: "transparent",
      color: dark ? "#e6e8eb" : "#1e293b",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": {
        backgroundColor: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    btnPrimary: {
      minWidth: 110,
      height: 36,
      padding: "0 18px",
      borderRadius: 10,
      border: "none",
      color: "#ffffff",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      boxShadow: "none",
      textTransform: "none",
      fontSize: "0.95rem",
      fontWeight: 500,
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": {
        boxShadow: "none",
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    progressBlock: {
      padding: "8px 0 16px",
    },
    progressBar: {
      height: 10,
      borderRadius: 8,
      backgroundColor: dark ? "rgba(255,255,255,0.06)" : "#eef2f7",
      "& .MuiLinearProgress-bar": {
        borderRadius: 8,
        background: "linear-gradient(90deg, #4096dc 0%, #1565c0 100%)",
      },
    },
    progressText: {
      fontSize: 13,
      color: dark ? "#e6e8eb" : "#1e293b",
      marginTop: 10,
      textAlign: "center",
    },
    statsRow: {
      display: "flex",
      justifyContent: "space-around",
      gap: 8,
      marginTop: 16,
      padding: "12px 8px",
      borderRadius: 10,
      backgroundColor: dark ? "rgba(255,255,255,0.03)" : "#f8fafc",
    },
    statItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 2,
      flex: 1,
    },
    statValue: {
      fontSize: 18,
      fontWeight: 700,
      color: dark ? "#f5f5f5" : "#1e293b",
    },
    statLabel: {
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.1,
      textTransform: "uppercase",
      color: dark ? "#8b94a3" : "#64748B",
    },
    completeBlock: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "12px 0",
    },
    completeIcon: {
      color: "#4096dc",
      marginBottom: 8,
    },
    completeTitle: {
      fontSize: "1.05rem",
      fontWeight: 700,
      color: dark ? "#f5f5f5" : "#1e293b",
      marginBottom: 4,
    },
    truncatedWarn: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 10,
      marginTop: 14,
      backgroundColor: dark ? "rgba(252,211,77,0.08)" : "#FEF3C7",
      color: dark ? "#fbbf24" : "#B45309",
      fontSize: 12,
      lineHeight: 1.5,
    },
  };
});

const SyncHistoryModal = ({ open, ticketId, onClose }) => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const companyId = localStorage.getItem("companyId");

  const [modalState, setModalState] = useState(STATES.FORM);
  const [daysPreset, setDaysPreset] = useState("30");
  const [customDays, setCustomDays] = useState("");
  const [downloadMedia, setDownloadMedia] = useState(false);
  const [loading, setLoading] = useState(false);

  const [progress, setProgress] = useState({
    processed: 0,
    total: 500,
    percentage: 0,
    imported: 0,
    skipped: 0,
    errors: 0,
    mediaSkipped: 0,
  });
  const [finalStats, setFinalStats] = useState(null);

  const socketRef = useRef(null);
  const handlersRef = useRef({});

  const resetState = useCallback(() => {
    setModalState(STATES.FORM);
    setDaysPreset("30");
    setCustomDays("");
    setDownloadMedia(false);
    setLoading(false);
    setProgress({
      processed: 0, total: 500, percentage: 0,
      imported: 0, skipped: 0, errors: 0, mediaSkipped: 0,
    });
    setFinalStats(null);
  }, []);

  const detachSocket = useCallback(() => {
    const socket = socketRef.current;
    const h = handlersRef.current;
    if (socket && h.progress) socket.off(`company-${companyId}-ticket-sync-progress`, h.progress);
    if (socket && h.complete) socket.off(`company-${companyId}-ticket-sync-complete`, h.complete);
    if (socket && h.error) socket.off(`company-${companyId}-ticket-sync-error`, h.error);
    handlersRef.current = {};
  }, [companyId]);

  useEffect(() => {
    return () => detachSocket();
  }, [detachSocket]);

  useEffect(() => {
    if (!open) {
      resetState();
      detachSocket();
    }
  }, [open, resetState, detachSocket]);

  const getDaysValue = () => {
    if (daysPreset === "custom") {
      const n = Number(customDays);
      if (!Number.isFinite(n) || n < 1) return null;
      return Math.min(Math.floor(n), MAX_DAYS);
    }
    return Number(daysPreset);
  };

  const handleStart = async () => {
    const days = getDaysValue();
    if (days === null) return;

    // Prepara socket listeners ANTES de disparar a request — evita perder
    // o primeiro progress event se o backend emitir rapidamente.
    const socket = socketManager.getSocket(companyId);
    socketRef.current = socket;

    const handleProgress = (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;
      setProgress({
        processed: data.processed || 0,
        total: data.total || 500,
        percentage: data.percentage || 0,
        imported: data.imported || 0,
        skipped: data.skipped || 0,
        errors: data.errors || 0,
        mediaSkipped: data.mediaSkipped || 0,
      });
    };
    const handleComplete = (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;
      setFinalStats(data);
      setModalState(STATES.COMPLETE);
      detachSocket();
    };
    const handleError = (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;
      toastError(new Error(data?.error || "Falha na sincronização"));
      setModalState(STATES.FORM);
      setLoading(false);
      detachSocket();
    };

    socket.on(`company-${companyId}-ticket-sync-progress`, handleProgress);
    socket.on(`company-${companyId}-ticket-sync-complete`, handleComplete);
    socket.on(`company-${companyId}-ticket-sync-error`, handleError);
    handlersRef.current = {
      progress: handleProgress,
      complete: handleComplete,
      error: handleError,
    };

    setLoading(true);
    try {
      await api.post(`/tickets/${ticketId}/sync-history`, { days, downloadMedia });
      setModalState(STATES.SYNCING);
    } catch (err) {
      toastError(err);
      setLoading(false);
      detachSocket();
    }
  };

  // Fechar em SYNCING mantém o sync no backend; só detacha listeners locais.
  const handleClose = useCallback(() => {
    const fn = onClose;
    if (fn) fn();
  }, [onClose]);

  const renderForm = () => (
    <>
      <Typography className={classes.description}>
        {i18n.t("syncHistoryModal.description")}
      </Typography>

      <Typography className={classes.sectionLabel}>
        {i18n.t("syncHistoryModal.fields.period")}
      </Typography>
      <RadioGroup
        className={classes.radioGroup}
        value={daysPreset}
        onChange={(e) => setDaysPreset(e.target.value)}
      >
        {DAYS_PRESETS.map((d) => (
          <FormControlLabel
            key={d}
            value={String(d)}
            control={<Radio size="small" style={{ color: "#4096dc" }} />}
            label={i18n.t(`syncHistoryModal.fields.days${d}`)}
            className={`${classes.radioItem} ${daysPreset === String(d) ? classes.radioItemActive : ""}`}
          />
        ))}
        <FormControlLabel
          value="custom"
          control={<Radio size="small" style={{ color: "#4096dc" }} />}
          label={i18n.t("syncHistoryModal.fields.custom")}
          className={`${classes.radioItem} ${daysPreset === "custom" ? classes.radioItemActive : ""}`}
        />
      </RadioGroup>

      <Collapse in={daysPreset === "custom"} timeout={220} unmountOnExit>
        <div className={classes.customField}>
          <span className={classes.customFieldIconBox}>
            <Calendar size={16} strokeWidth={1.8} />
          </span>
          <div className={classes.customFieldLabelBlock}>
            <span className={classes.customFieldLabel}>
              {i18n.t("syncHistoryModal.fields.customInput")}
            </span>
            <span className={classes.customFieldHint}>
              {i18n.t("syncHistoryModal.fields.customHint", { max: MAX_DAYS })}
            </span>
          </div>
          <div className={classes.customFieldInputWrap}>
            <input
              type="number"
              className={classes.customFieldInput}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              min={1}
              max={MAX_DAYS}
              placeholder="30"
              autoFocus
            />
            <span className={classes.customFieldSuffix}>
              {i18n.t("syncHistoryModal.fields.daysSuffix")}
            </span>
          </div>
        </div>
      </Collapse>

      <div className={classes.switchRow}>
        <div className={classes.switchLabel}>
          <span className={classes.switchLabelTitle}>
            {i18n.t("syncHistoryModal.fields.downloadMedia")}
          </span>
          <span className={classes.switchLabelHint}>
            {i18n.t("syncHistoryModal.fields.downloadMediaHint")}
          </span>
        </div>
        <Switch
          checked={downloadMedia}
          onChange={(e) => setDownloadMedia(e.target.checked)}
          color="primary"
        />
      </div>
    </>
  );

  const renderSyncing = () => (
    <div className={classes.progressBlock}>
      <LinearProgress
        className={classes.progressBar}
        variant="determinate"
        value={progress.percentage}
      />
      <Typography className={classes.progressText}>
        {i18n.t("syncHistoryModal.progress.syncing", {
          processed: progress.processed,
          total: progress.total,
        })}
      </Typography>
      <div className={classes.statsRow}>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{progress.imported}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.imported")}
          </span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{progress.skipped}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.skipped")}
          </span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{progress.errors}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.errors")}
          </span>
        </div>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className={classes.completeBlock}>
      <CheckCircle size={52} strokeWidth={1.8} className={classes.completeIcon} />
      <div className={classes.completeTitle}>
        {i18n.t("syncHistoryModal.progress.complete")}
      </div>
      <div className={classes.statsRow} style={{ width: "100%" }}>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{finalStats?.imported || 0}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.imported")}
          </span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{finalStats?.skipped || 0}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.skipped")}
          </span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{finalStats?.errors || 0}</span>
          <span className={classes.statLabel}>
            {i18n.t("syncHistoryModal.stats.errors")}
          </span>
        </div>
      </div>
      {finalStats?.truncated && (
        <div className={classes.truncatedWarn}>
          <AlertTriangle size={16} strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{i18n.t("syncHistoryModal.progress.truncated")}</span>
        </div>
      )}
    </div>
  );

  const canStart =
    !loading &&
    (daysPreset !== "custom" ||
      (customDays && Number(customDays) >= 1 && Number(customDays) <= MAX_DAYS));

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <span className={classes.titleIconBox}>
            <DownloadCloud size={18} strokeWidth={1.8} />
          </span>
          <span className={classes.title}>
            {i18n.t("syncHistoryModal.title")}
          </span>
        </div>
        <IconButton className={classes.closeBtn} onClick={handleClose} size="small">
          <X size={18} strokeWidth={1.8} />
        </IconButton>
      </div>

      <div className={classes.body}>
        {modalState === STATES.FORM && renderForm()}
        {modalState === STATES.SYNCING && renderSyncing()}
        {modalState === STATES.COMPLETE && renderComplete()}
      </div>

      <div className={classes.footer}>
        {modalState === STATES.FORM && (
          <>
            <button className={classes.btnGhost} onClick={handleClose} disabled={loading}>
              {i18n.t("syncHistoryModal.buttons.cancel")}
            </button>
            <button className={classes.btnPrimary} onClick={handleStart} disabled={!canStart}>
              {i18n.t("syncHistoryModal.buttons.sync")}
            </button>
          </>
        )}
        {modalState === STATES.SYNCING && (
          <button className={classes.btnGhost} onClick={handleClose}>
            {i18n.t("syncHistoryModal.buttons.background")}
          </button>
        )}
        {modalState === STATES.COMPLETE && (
          <button className={classes.btnPrimary} onClick={handleClose}>
            {i18n.t("syncHistoryModal.buttons.close")}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default SyncHistoryModal;
